import React from "react";
import { App as AntdApp, Button, Card, Descriptions, Popconfirm, Space, Tag, Tooltip, Typography } from "antd";
import { ArrowLeftOutlined, CopyOutlined, DeleteOutlined, OrderedListOutlined, PlusOutlined } from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";

import { useSSE } from "@modules/realtime/hooks/useSSE.js";
import FlyersTable from "@modules/flyers/components/FlyersTable.jsx";
import { deletePromo, getPromo, listPromoFlyers } from "../api/promoApi.js";
import CreateFlyerInPromoModal from "../components/CreateFlyerInPromoModal.jsx";
import CreateVariantInPromoModal from "../components/CreateVariantInPromoModal.jsx";
import SerialsDrawer from "../components/SerialsDrawer.jsx";

const { Title } = Typography;
const fmt = (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "—");

export default function PromoDetailPage() {
	const { promoId } = useParams();
	const navigate = useNavigate();
	// Ritorno all'elenco con la stessa ricerca e gli stessi filtri da cui si è aperta la promo
	// (li passa PromosPage nello stato di navigazione; aprendo un link diretto non ci sono).
	const location = useLocation();
	const listSearch = location.state?.listSearch;
	const listUrl = listSearch ? `/promos?${listSearch}` : "/promos";
	const { message } = AntdApp.useApp();

	const [promo, setPromo] = React.useState(null);
	const [flyers, setFlyers] = React.useState([]);
	const [loading, setLoading] = React.useState(false);
	const [openFlyer, setOpenFlyer] = React.useState(false);
	const [openVariant, setOpenVariant] = React.useState(false);
	const [openSerials, setOpenSerials] = React.useState(false);

	const fetchAll = async () => {
		setLoading(true);
		try {
			const [p, fl] = await Promise.all([getPromo(promoId), listPromoFlyers(promoId)]);
			setPromo(p);
			setFlyers(fl);
		}
		catch (error) {
			message.error(error?.response?.data?.error || "Impossibile caricare la promo");
		}
		finally {
			setLoading(false);
		}
	};

	React.useEffect(() => { fetchAll(); }, [promoId]);

	useSSE("flyers", {
		"flyer.status.changed": (p) => {
			if (!p?.flyerId) return;
			setFlyers((prev) => prev.map((f) => ((f._id || f.id) === p.flyerId ? { ...f, status: p.status, processingProgress: p.processingProgress ?? f.processingProgress } : f)));
		},
		"flyer.card.created": (p) => {
			if (!p?.flyerId) return;
			setFlyers((prev) => prev.map((f) => ((f._id || f.id) === p.flyerId ? { ...f, cardsCount: (f.cardsCount || 0) + 1 } : f)));
		},
		"flyer.card.deleted": (p) => {
			if (!p?.flyerId) return;
			setFlyers((prev) => prev.map((f) => ((f._id || f.id) === p.flyerId ? { ...f, cardsCount: Math.max(0, (f.cardsCount || 1) - 1) } : f)));
		},
		"flyer.refs.updated": (p) => {
			if (!p?.flyerId) return;
			setFlyers((prev) => prev.map((f) => ((f._id || f.id) === p.flyerId ? { ...f, refsCount: p.count } : f)));
		}
	});

	const hasValidated = React.useMemo(
		() => flyers.some((f) => f.status === "DONE" || f.status === "READY"),
		[flyers]
	);

	const activeFlyersCount = React.useMemo(
		() => flyers.filter((f) => !f.deletedAt).length,
		[flyers]
	);

	const handleDelete = async () => {
		try {
			const data = await deletePromo(promoId);
			const n = data?.cascadedFlyers ?? 0;
			message.success(n > 0 ? `Promo eliminata. ${n} volantini spostati nel cestino.` : "Promo eliminata.");
			navigate(listUrl);
		}
		catch (error) {
			message.error(error?.response?.data?.error || "Errore eliminazione promo");
		}
	};

	const handleManage = (flyer) => {
		navigate(`/flyermanage/${flyer._id || flyer.id}`, { state: { flyerName: flyer.name || "Volantino" } });
	};

	return (
		<Space direction="vertical" style={{ width: "100%" }} size="large">
			<Card className="card-elev">
				<Space style={{ width: "100%", justifyContent: "space-between" }} wrap>
					<Space>
						<Button icon={<ArrowLeftOutlined />} onClick={() => navigate(listUrl)} />
						<Title level={4} style={{ margin: 0 }}>{promo?.name || "Promo"} {promo?.isLegacy && <Tag>Legacy</Tag>}</Title>
					</Space>
					<Space wrap>
						<Button type="primary" icon={<PlusOutlined />} onClick={() => setOpenFlyer(true)} disabled={!promo || promo?.isLegacy}>
							Nuovo Volantino
						</Button>
						<Tooltip title={hasValidated ? "" : "Serve almeno un volantino validato (DONE/READY)"}>
							<Button icon={<CopyOutlined />} onClick={() => setOpenVariant(true)} disabled={!hasValidated || promo?.isLegacy}>
								Nuova Variante
							</Button>
						</Tooltip>
						<Button icon={<OrderedListOutlined />} onClick={() => setOpenSerials(true)} disabled={!promo}>
							Seriali
						</Button>
						{promo && !promo.isLegacy && (
							<Popconfirm
								title="Eliminare la promo?"
								description={activeFlyersCount > 0
									? `${activeFlyersCount} volantini verranno spostati nel cestino. Potrai ripristinarli ripristinando la promo.`
									: "La promo verrà spostata nel cestino."}
								okText="Elimina"
								okButtonProps={{ danger: true }}
								cancelText="Annulla"
								onConfirm={handleDelete}
								placement="bottomRight"
							>
								<Button danger icon={<DeleteOutlined />}>Elimina Promo</Button>
							</Popconfirm>
						)}
					</Space>
				</Space>

				{promo && (
					<Descriptions size="small" column={2} style={{ marginTop: 16 }}>
						<Descriptions.Item label="Consorzio">{promo.consorzioName || "—"}</Descriptions.Item>
						<Descriptions.Item label="Cooperativa">{promo.cooperativaName || "—"}</Descriptions.Item>
						<Descriptions.Item label="Sellout">{fmt(promo.sellOutFrom)} → {fmt(promo.sellOutTo)}</Descriptions.Item>
						<Descriptions.Item label="Data Avvio">{fmt(promo.startDate)}</Descriptions.Item>
						{promo.description && <Descriptions.Item label="Descrizione" span={2}>{promo.description}</Descriptions.Item>}
					</Descriptions>
				)}
			</Card>

			<FlyersTable
				flyers={flyers}
				loading={loading}
				flat={true}
				onManage={handleManage}
				onRefresh={fetchAll}
			/>

			<CreateFlyerInPromoModal
				open={openFlyer}
				promo={promo}
				onClose={() => setOpenFlyer(false)}
				onFlyerCreated={(id) => { if (id) navigate(`/flyerpreprocess/${id}`); }}
			/>
			<CreateVariantInPromoModal
				open={openVariant}
				promo={promo}
				onClose={() => setOpenVariant(false)}
				onVariantCreated={() => fetchAll()}
			/>
			<SerialsDrawer
				open={openSerials}
				promoId={promoId}
				promoName={promo?.name}
				onClose={() => setOpenSerials(false)}
			/>
		</Space>
	);
}
